<template>
  <div class="fraud-config-page">
    <b-card>
      <b-card-header class="pb-0">
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-start w-100">
          <div>
            <h4 class="mb-0">
              {{ $t('ตั้งค่าระบบตรวจจับการปั้มหวย') }}
            </h4>
            <small class="text-secondary">{{ $t('กำหนดเกณฑ์การตรวจจับพฤติกรรมที่น่าสงสัยในระบบหวย') }}</small>
          </div>
          <div
            v-if="lastSaved || updatedBy"
            class="text-secondary small flex-shrink-0"
          >
            {{ $t('อัพเดตโดย') }} {{ updatedBy || '-' }} : {{ lastSaved || '-' }}
          </div>
        </div>
      </b-card-header>

      <hr class="header-divider">

      <b-card-body>
        <b-overlay
          :show="loading"
          rounded="sm"
        >
          <b-form @submit.prevent="saveConfig">
            <!-- Global Settings Section -->
            <b-row>
              <b-col
                md="6"
              >
                <b-card
                  class="border mb-1"
                  no-body
                >
                  <b-card-body>
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <h5 class="mb-0">
                          {{ $t('ตั้งค่าทั่วไป') }}
                          <feather-icon
                            v-b-tooltip.hover
                            icon="HelpCircleIcon"
                            size="14"
                            :title="$t('การตั้งค่าพื้นฐานของระบบตรวจจับ')"
                          />
                        </h5>
                        <small class="text-secondary">{{ $t('เปิด/ปิดระบบและกำหนดช่วงเวลา') }}</small>
                      </div>
                      <b-form-checkbox
                        v-model="config.enabled"
                        switch
                      />
                    </div>

                    <b-form-group
                      :label="$t('ช่วงเวลาย้อนหลัง (ชั่วโมง)')"
                      label-for="lookbackHours"
                    >
                      <b-form-input
                        id="lookbackHours"
                        v-model.number="config.lookbackHours"
                        type="number"
                        min="1"
                        max="168"
                        :disabled="!config.enabled"
                        placeholder="1-168"
                      />
                      <small class="text-secondary">{{ $t('ตรวจสอบข้อมูลย้อนหลัง 1-168 ชั่วโมง') }}</small>
                    </b-form-group>
                  </b-card-body>
                </b-card>
              </b-col>
            </b-row>

            <hr>

            <!-- Detection Patterns Section -->
            <h5 class="mt-2 mb-2">
              {{ $t('ตั้งค่ารูปแบบการตรวจจับ') }}
            </h5>
            <b-row>
              <!-- IP Clustering -->
              <b-col
                md="6"
                class="mb-2"
              >
                <b-card
                  class="border h-100"
                  no-body
                  :class="{ 'opacity-50': !config.enabled }"
                >
                  <b-card-body>
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <h5 class="mb-0">
                          {{ $t('IP Clustering') }}
                          <feather-icon
                            v-b-tooltip.hover
                            icon="HelpCircleIcon"
                            size="14"
                            :title="$t('ตรวจจับผู้ใช้หลายคนที่เข้าจาก IP เดียวกัน อาจเป็นสัญญาณของการใช้หลาย account')"
                          />
                        </h5>
                        <small class="text-secondary">{{ $t('ตรวจจับผู้ใช้หลายคนจาก IP เดียวกัน') }}</small>
                      </div>
                      <b-form-checkbox
                        v-model="config.ipDetectionEnabled"
                        switch
                        :disabled="!config.enabled"
                      />
                    </div>

                    <b-form-group
                      :label="$t('จำนวน users ต่อ IP')"
                      label-for="minAccountCount"
                    >
                      <b-form-input
                        id="minAccountCount"
                        v-model.number="config.minAccountCount"
                        type="number"
                        min="1"
                        :disabled="!config.enabled || !config.ipDetectionEnabled"
                      />
                      <small class="text-secondary">{{ $t('ถ้ามี users มากกว่าค่านี้จาก IP เดียวกัน จะถือว่าน่าสงสัย') }}</small>
                    </b-form-group>

                    <b-form-group
                      :label="$t('ช่วงเวลา (นาที)')"
                      label-for="timeWindowMinutes"
                    >
                      <b-form-input
                        id="timeWindowMinutes"
                        v-model.number="config.timeWindowMinutes"
                        type="number"
                        min="1"
                        :disabled="!config.enabled || !config.ipDetectionEnabled"
                      />
                      <small class="text-secondary">{{ $t('ช่วงเวลาที่ใช้ตรวจสอบ') }}</small>
                    </b-form-group>
                  </b-card-body>
                </b-card>
              </b-col>

              <!-- Sequential Numbers -->
              <b-col
                md="6"
                class="mb-2"
              >
                <b-card
                  class="border h-100"
                  no-body
                  :class="{ 'opacity-50': !config.enabled }"
                >
                  <b-card-body>
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <h5 class="mb-0">
                          {{ $t('Sequential Numbers') }}
                          <feather-icon
                            v-b-tooltip.hover
                            icon="HelpCircleIcon"
                            size="14"
                            :title="$t('ตรวจจับการเดิมพันเลขต่อเนื่อง เช่น 01, 02, 03, 04, 05...')"
                          />
                        </h5>
                        <small class="text-secondary">{{ $t('ตรวจจับการเดิมพันเลขต่อเนื่อง') }}</small>
                      </div>
                      <b-form-checkbox
                        v-model="config.sequentialNumbersEnabled"
                        switch
                        :disabled="!config.enabled"
                      />
                    </div>

                    <b-form-group
                      :label="$t('จำนวนเลขต่อเนื่อง')"
                      label-for="sequentialNumbersThreshold"
                    >
                      <b-form-input
                        id="sequentialNumbersThreshold"
                        v-model.number="config.sequentialNumbersThreshold"
                        type="number"
                        min="1"
                        :disabled="!config.enabled || !config.sequentialNumbersEnabled"
                      />
                      <small class="text-secondary">{{ $t('จำนวนเลขต่อเนื่องขั้นต่ำที่ถือว่าน่าสงสัย') }}</small>
                    </b-form-group>
                  </b-card-body>
                </b-card>
              </b-col>

              <!-- High Volume -->
              <b-col
                md="6"
                class="mb-2"
              >
                <b-card
                  class="border h-100"
                  no-body
                  :class="{ 'opacity-50': !config.enabled }"
                >
                  <b-card-body>
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <h5 class="mb-0">
                          {{ $t('High Volume') }}
                          <feather-icon
                            v-b-tooltip.hover
                            icon="HelpCircleIcon"
                            size="14"
                            :title="$t('ตรวจจับยอดเดิมพันสูงผิดปกติ อาจเป็นการพยายามใช้ช่องว่าง')"
                          />
                        </h5>
                        <small class="text-secondary">{{ $t('ตรวจจับยอดเดิมพันสูงผิดปกติ') }}</small>
                      </div>
                      <b-form-checkbox
                        v-model="config.maxNumbersEnabled"
                        switch
                        :disabled="!config.enabled"
                      />
                    </div>

                    <b-form-group
                      :label="$t('จำนวนเลขสูงสุดต่อผู้ใช้')"
                      label-for="maxNumbersPerUser"
                    >
                      <b-form-input
                        id="maxNumbersPerUser"
                        v-model.number="config.maxNumbersPerUser"
                        type="number"
                        min="1"
                        :disabled="!config.enabled || !config.maxNumbersEnabled"
                      />
                      <small class="text-secondary">{{ $t('เล่นเกินกี่เลขถือว่าน่าสงสัย') }}</small>
                    </b-form-group>
                  </b-card-body>
                </b-card>
              </b-col>

              <!-- Coverage -->
              <b-col
                md="6"
                class="mb-2"
              >
                <b-card
                  class="border h-100"
                  no-body
                  :class="{ 'opacity-50': !config.enabled }"
                >
                  <b-card-body>
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <h5 class="mb-0">
                          {{ $t('Coverage') }}
                          <feather-icon
                            v-b-tooltip.hover
                            icon="HelpCircleIcon"
                            size="14"
                            :title="$t('ตรวจจับการเดิมพันครอบคลุมหลายหมายเลข เช่น แทงครอบคลุม 80% ของตัวเลขทั้งหมด')"
                          />
                        </h5>
                        <small class="text-secondary">{{ $t('ตรวจจับการเดิมพันครอบคลุมหลายหมายเลข') }}</small>
                      </div>
                      <b-form-checkbox
                        v-model="config.coverageEnabled"
                        switch
                        :disabled="!config.enabled"
                      />
                    </div>

                    <b-form-group
                      :label="$t('% ความครอบคลุม')"
                      label-for="coverageThresholdPercent"
                    >
                      <b-input-group append="%">
                        <b-form-input
                          id="coverageThresholdPercent"
                          v-model.number="config.coverageThresholdPercent"
                          type="number"
                          min="0"
                          max="100"
                          :disabled="!config.enabled || !config.coverageEnabled"
                        />
                      </b-input-group>
                      <small class="text-secondary">{{ $t('เล่นครอบคลุมกี่ % ของเลขทั้งหมดถือว่าน่าสงสัย') }}</small>
                    </b-form-group>
                  </b-card-body>
                </b-card>
              </b-col>

              <!-- Repeat Betting -->
              <b-col
                md="6"
                class="mb-2"
              >
                <b-card
                  class="border h-100"
                  no-body
                  :class="{ 'opacity-50': !config.enabled }"
                >
                  <b-card-body>
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <h5 class="mb-0">
                          {{ $t('Repeat Betting') }}
                          <feather-icon
                            v-b-tooltip.hover
                            icon="HelpCircleIcon"
                            size="14"
                            :title="$t('ตรวจจับรูปแบบการเดิมพันซ้ำๆ เช่น แทงชุดเลขเดิมซ้ำหลายครั้ง')"
                          />
                        </h5>
                        <small class="text-secondary">{{ $t('ตรวจจับรูปแบบการเดิมพันซ้ำๆ') }}</small>
                      </div>
                      <b-form-checkbox
                        v-model="config.repeatBettingEnabled"
                        switch
                        :disabled="!config.enabled"
                      />
                    </div>

                    <b-form-group
                      :label="$t('จำนวนครั้งที่ซ้ำ')"
                      label-for="repeatBettingMinOccurrences"
                    >
                      <b-form-input
                        id="repeatBettingMinOccurrences"
                        v-model.number="config.repeatBettingMinOccurrences"
                        type="number"
                        min="1"
                        :disabled="!config.enabled || !config.repeatBettingEnabled"
                      />
                      <small class="text-secondary">{{ $t('เล่นซ้ำอย่างน้อยกี่ครั้งถือว่าน่าสงสัย') }}</small>
                    </b-form-group>

                    <b-form-group
                      :label="$t('ช่วงเวลา (วัน)')"
                      label-for="repeatBettingTimeWindowDays"
                    >
                      <b-form-input
                        id="repeatBettingTimeWindowDays"
                        v-model.number="config.repeatBettingTimeWindowDays"
                        type="number"
                        min="1"
                        :disabled="!config.enabled || !config.repeatBettingEnabled"
                      />
                      <small class="text-secondary">{{ $t('ช่วงเวลาที่ใช้ตรวจสอบ') }}</small>
                    </b-form-group>
                  </b-card-body>
                </b-card>
              </b-col>
            </b-row>

            <!-- Action Buttons -->
            <div class="text-right">
              <b-button
                variant="primary"
                type="submit"
                :disabled="loading"
              >
                <feather-icon
                  icon="SaveIcon"
                  class="mr-50"
                />
                {{ $t('บันทึกการตั้งค่า') }}
              </b-button>
            </div>
          </b-form>
        </b-overlay>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BInputGroup,
  BButton,
  BOverlay,
  VBTooltip,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import lottoService from '@/service/lottoService'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BInputGroup,
    BButton,
    BOverlay,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      loading: false,
      lastSaved: null,
      updatedBy: null,
      config: {
        enabled: true,
        lookbackHours: 24,
        ipDetectionEnabled: false,
        timeWindowMinutes: 30,
        minAccountCount: 3,
        sequentialNumbersEnabled: false,
        sequentialNumbersThreshold: 30,
        maxNumbersEnabled: false,
        maxNumbersPerUser: 50,
        coverageEnabled: false,
        coverageThresholdPercent: 70,
        repeatBettingEnabled: false,
        repeatBettingMinOccurrences: 3,
        repeatBettingTimeWindowDays: 7,
      },
      originalConfig: null,
    }
  },
  async mounted() {
    await this.loadConfig()
  },
  methods: {
    async loadConfig() {
      this.loading = true
      try {
        const res = await lottoService.getAuthen('fraud-detection/config')

        if (res.status === 200 && res.data) {
          this.config = {
            enabled: res.data.enabled ?? this.config.enabled,
            lookbackHours: res.data.lookbackHours ?? this.config.lookbackHours,
            ipDetectionEnabled: res.data.ipDetectionEnabled ?? this.config.ipDetectionEnabled,
            timeWindowMinutes: res.data.timeWindowMinutes ?? this.config.timeWindowMinutes,
            minAccountCount: res.data.minAccountCount ?? this.config.minAccountCount,
            sequentialNumbersEnabled: res.data.sequentialNumbersEnabled ?? this.config.sequentialNumbersEnabled,
            sequentialNumbersThreshold: res.data.sequentialNumbersThreshold ?? this.config.sequentialNumbersThreshold,
            maxNumbersEnabled: res.data.maxNumbersEnabled ?? this.config.maxNumbersEnabled,
            maxNumbersPerUser: res.data.maxNumbersPerUser ?? this.config.maxNumbersPerUser,
            coverageEnabled: res.data.coverageEnabled ?? this.config.coverageEnabled,
            coverageThresholdPercent: res.data.coverageThresholdPercent ?? this.config.coverageThresholdPercent,
            repeatBettingEnabled: res.data.repeatBettingEnabled ?? this.config.repeatBettingEnabled,
            repeatBettingMinOccurrences: res.data.repeatBettingMinOccurrences ?? this.config.repeatBettingMinOccurrences,
            repeatBettingTimeWindowDays: res.data.repeatBettingTimeWindowDays ?? this.config.repeatBettingTimeWindowDays,
          }

          this.originalConfig = JSON.parse(JSON.stringify(this.config))

          if (res.data.updatedAt) {
            this.lastSaved = this.formatDateTime(res.data.updatedAt)
          }
          if (res.data.updatedBy) {
            this.updatedBy = res.data.updatedBy
          }
        } else {
          this.originalConfig = JSON.parse(JSON.stringify(this.config))
        }
      } catch (error) {
        this.originalConfig = JSON.parse(JSON.stringify(this.config))
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('ไม่สามารถเชื่อมต่อ API ได้ ใช้ค่าเริ่มต้น'),
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
      } finally {
        this.loading = false
      }
    },

    async saveConfig() {
      // Validate
      if (this.config.enabled) {
        if (!this.config.lookbackHours || this.config.lookbackHours < 1 || this.config.lookbackHours > 168) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('ช่วงเวลาย้อนหลังต้องอยู่ระหว่าง 1-168 ชั่วโมง'),
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
          return
        }
      }

      this.$bvModal.msgBoxConfirm(this.$t('คุณต้องการบันทึกการตั้งค่าระบบตรวจจับการปั้มใช่หรือไม่?'), {
        title: this.$t('ยืนยันการบันทึก'),
        okVariant: 'primary',
        okTitle: this.$t('ยืนยัน'),
        cancelTitle: this.$t('ยกเลิก'),
        cancelVariant: 'outline-secondary',
        centered: true,
      }).then(async value => {
        if (value) {
          this.loading = true
          try {
            const res = await lottoService.putAuthen(
              'fraud-detection/config',
              this.config,
            )

            if (res.status === 200) {
              this.originalConfig = JSON.parse(JSON.stringify(this.config))
              this.lastSaved = this.formatDateTime(new Date())
              if (res.data && res.data.updatedBy) {
                this.updatedBy = res.data.updatedBy
              }

              this.$toast({
                component: ToastificationContent,
                props: {
                  title: this.$t('บันทึกการตั้งค่าสำเร็จ'),
                  icon: 'CheckCircleIcon',
                  variant: 'success',
                },
              })
            } else {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: this.$t('ไม่สามารถบันทึกการตั้งค่าได้: {message}', {
                    message: res.data || this.$t('เกิดข้อผิดพลาด'),
                  }),
                  icon: 'AlertTriangleIcon',
                  variant: 'danger',
                },
              })
            }
          } catch (error) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: this.$t('เกิดข้อผิดพลาดในการบันทึกข้อมูล'),
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
          } finally {
            this.loading = false
          }
        }
      })
    },

    formatDateTime(dateTime) {
      const date = new Date(dateTime)
      const day = date.getDate().toString().padStart(2, '0')
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const year = date.getFullYear()
      const hours = date.getHours().toString().padStart(2, '0')
      const minutes = date.getMinutes().toString().padStart(2, '0')
      const seconds = date.getSeconds().toString().padStart(2, '0')

      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`
    },
  },
}
</script>

<style scoped>
.opacity-50 {
  opacity: 0.5;
}

.header-divider {
  margin: 1rem -1.25rem 0;
}
</style>

<style>
/* Dark mode - improve text-secondary contrast */
.dark-layout .fraud-config-page .text-secondary {
  color: #9ca0a8 !important;
}

/* Mobile responsive - reduce padding on small screens (unscoped to override Bootstrap) */
@media (max-width: 767.98px) {
  .fraud-config-page > .card > .card-body {
    padding: 0 !important;
  }

  .fraud-config-page .header-divider {
    margin: 1rem 0 0;
  }
}
</style>
